<template>
  <div class="usage-page">
    <div class="usage-page__inner">
      <header class="usage-page__header">
        <v-btn text class="usage-page__back" @click="$router.push('/tracey')">
          <v-icon left small>mdi-arrow-left</v-icon>
          Back to Tracey
        </v-btn>
        <h1 class="usage-page__title">Tracey usage</h1>
        <p class="usage-page__subtitle">
          Your current credits, daily usage, and what each conversation cost.
        </p>
      </header>

      <div v-if="topupBanner" class="usage-page__banner" :class="`usage-page__banner--${topupBanner.kind}`">
        {{ topupBanner.text }}
      </div>

      <!-- Current credits: Sprint and Ultra each have their own wallet -->
      <div class="usage-page__wallets">
        <div v-for="w in wallets" :key="w.key" class="wallet-card">
          <div class="wallet-card__head">
            <span class="wallet-card__name">{{ w.label }}</span>
          </div>
          <div v-if="w.loading" class="wallet-card__loading">Loading…</div>
          <template v-else-if="w.snapshot">
            <div class="wallet-card__balance">
              {{ formatCredits(w.snapshot.balance_credits) }}
              <span class="wallet-card__allowance">credits available</span>
            </div>
            <div class="wallet-card__bar">
              <div
                class="wallet-card__bar-fill"
                :class="{ 'wallet-card__bar-fill--empty': w.snapshot.balance_credits <= 0 }"
                :style="{ width: barWidth(w.snapshot) + '%', background: w.color }"
              />
            </div>
            <div v-if="w.snapshot.period_end" class="wallet-card__reset">
              {{ formatCredits(w.snapshot.allowance_credits) }} monthly credits provided on
              {{ formatResetDate(w.snapshot.period_end) }}
            </div>
          </template>
          <div v-else class="wallet-card__loading">Couldn't load balance.</div>
        </div>
      </div>

      <!-- Daily usage chart -->
      <section class="usage-page__section">
        <div class="section-head">
          <h2>Daily usage</h2>
          <div class="chart-legend">
            <span class="chart-legend__item"><i :style="{ background: COLOR_ULTRA }" />Ultra</span>
            <span class="chart-legend__item"><i :style="{ background: COLOR_SPRINT }" />Sprint</span>
          </div>
        </div>
        <div class="chart-card">
          <div v-if="dailyLoading" class="chart-card__loading">Loading…</div>
          <div v-else-if="!dailyDays.length" class="chart-card__loading">No usage yet.</div>
          <div v-else class="daily-chart">
            <div class="daily-chart__bars">
              <div
                v-for="d in dailyDays"
                :key="d.date"
                class="daily-chart__col"
                @mouseenter="hoveredDay = d"
                @mouseleave="hoveredDay = null"
              >
                <div
                  v-if="hoveredDay === d"
                  class="daily-chart__tooltip"
                >
                  <div class="daily-chart__tooltip-date">{{ d.label }}</div>
                  <div><i :style="{ background: COLOR_ULTRA }" /> Ultra: {{ formatCredits(d.ultra) }}</div>
                  <div><i :style="{ background: COLOR_SPRINT }" /> Sprint: {{ formatCredits(d.sprint) }}</div>
                </div>
                <div class="daily-chart__stack" :style="{ height: chartHeight + 'px' }">
                  <div
                    class="daily-chart__seg"
                    :style="{ height: d.sprintPx + 'px', background: COLOR_SPRINT }"
                  />
                  <div
                    class="daily-chart__seg daily-chart__seg--top"
                    :style="{ height: d.ultraPx + 'px', background: COLOR_ULTRA }"
                  />
                </div>
                <div class="daily-chart__label">{{ d.tickLabel }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Query history, grouped by conversation thread -->
      <section class="usage-page__section">
        <div class="section-head">
          <h2>Conversations</h2>
          <span class="section-note">multiple queries in one chat show as a single total</span>
        </div>
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          class="usage-page__table elevation-1"
          no-data-text="No conversations yet."
          loading-text="Loading usage…"
        >
          <template v-slot:item.last_active="{ item }">
            {{ formatDate(item.last_active) }}
          </template>

          <template v-slot:item.title="{ item }">
            <span class="usage-page__query" :title="item.title">{{ item.title }}</span>
          </template>

          <template v-slot:item.query_count="{ item }">
            {{ item.query_count }}
          </template>

          <template v-slot:item.credits_ultra="{ item }">
            <span v-if="item.credits_ultra > 0" class="usage-page__credits-ultra">{{ formatCredits(item.credits_ultra) }}</span>
            <span v-else class="usage-page__muted">—</span>
          </template>

          <template v-slot:item.credits_sprint="{ item }">
            <span v-if="item.credits_sprint > 0" class="usage-page__credits-sprint">{{ formatCredits(item.credits_sprint) }}</span>
            <span v-else class="usage-page__muted">—</span>
          </template>
        </v-data-table>

        <p v-if="loadError" class="usage-page__error">
          Couldn't load usage. Please try again.
        </p>
      </section>

      <!-- Raw ledger: grants, per-query debits, resets, top-ups -->
      <section class="usage-page__section">
        <div class="section-head">
          <h2>Transactions</h2>
          <span class="section-note">every credit movement on your wallets</span>
        </div>
        <v-data-table
          :headers="transactionHeaders"
          :items="transactions"
          :loading="transactionsLoading"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          class="usage-page__table elevation-1"
          no-data-text="No transactions yet."
          loading-text="Loading transactions…"
        >
          <template v-slot:item.created="{ item }">
            {{ formatDate(item.created) }}
          </template>

          <template v-slot:item.agent_version="{ item }">
            <v-chip
              v-if="item.agent_version && item.agent_version !== 'legacy'"
              x-small
              :color="item.agent_version === 'ultra' ? '#ffe4ee' : '#e0f2fe'"
              :text-color="item.agent_version === 'ultra' ? '#a3134f' : '#075985'"
            >
              {{ item.agent_version === 'ultra' ? 'Ultra' : 'Sprint' }}
            </v-chip>
            <span v-else class="usage-page__muted">—</span>
          </template>

          <template v-slot:item.reason="{ item }">
            <span :title="item.note || ''">{{ reasonLabel(item.reason) }}</span>
          </template>

          <template v-slot:item.delta_credits="{ item }">
            <span :class="item.delta_credits >= 0 ? 'usage-page__amount--positive' : 'usage-page__amount--negative'">
              {{ item.delta_credits >= 0 ? '+' : '' }}{{ formatCredits(item.delta_credits) }}
            </span>
          </template>

          <template v-slot:item.balance_after="{ item }">
            {{ formatCredits(item.balance_after) }}
          </template>
        </v-data-table>
      </section>
    </div>
  </div>
</template>

<script>
import {
  fetchTraceyUsageHistory,
  fetchTraceyUsageDaily,
  fetchTraceyUsage,
  fetchTraceyTransactions,
} from '../components/APIManager.js';

const COLOR_ULTRA = '#ff1463';
const COLOR_SPRINT = '#0891b2';
const CHART_HEIGHT = 140;

export default {
  name: 'UsagePage',
  props: {
    // Passed from App.vue <router-view :drawer="drawer" /> on shell routes.
    drawer: { type: Boolean, default: false },
  },
  data() {
    return {
      COLOR_ULTRA,
      COLOR_SPRINT,
      chartHeight: CHART_HEIGHT,
      items: [],
      loading: false,
      loadError: false,
      dailyRaw: [],
      dailyLoading: false,
      hoveredDay: null,
      walletSnapshots: { sprint: null, ultra: null },
      walletLoading: { sprint: true, ultra: true },
      topupBanner: null, // { kind: 'success'|'cancelled', text } after returning from Stripe
      headers: [
        { text: 'Conversation', value: 'title', sortable: false },
        { text: 'Queries', value: 'query_count', align: 'end', width: 90 },
        { text: 'Ultra credits', value: 'credits_ultra', align: 'end', width: 120 },
        { text: 'Sprint credits', value: 'credits_sprint', align: 'end', width: 130 },
        { text: 'Last active', value: 'last_active', width: 170 },
      ],
      transactions: [],
      transactionsLoading: false,
      transactionHeaders: [
        { text: 'Date', value: 'created', width: 170 },
        { text: 'Agent', value: 'agent_version', sortable: false, width: 100 },
        { text: 'Type', value: 'reason', sortable: false },
        { text: 'Amount', value: 'delta_credits', align: 'end', width: 110 },
        { text: 'Balance after', value: 'balance_after', align: 'end', width: 130 },
      ],
    };
  },
  computed: {
    wallets() {
      return [
        {
          key: 'ultra', label: 'Tracey Ultra', color: COLOR_ULTRA,
          snapshot: this.walletSnapshots.ultra, loading: this.walletLoading.ultra,
        },
        {
          key: 'sprint', label: 'Tracey Sprint', color: COLOR_SPRINT,
          snapshot: this.walletSnapshots.sprint, loading: this.walletLoading.sprint,
        },
      ];
    },
    dailyDays() {
      const max = Math.max(1, ...this.dailyRaw.map((d) => (Number(d.sprint) || 0) + (Number(d.ultra) || 0)));
      return this.dailyRaw.map((d, idx) => {
        const sprint = Number(d.sprint) || 0;
        const ultra = Number(d.ultra) || 0;
        const dt = new Date(`${d.date}T00:00:00Z`);
        const showTick = idx % 5 === 0 || idx === this.dailyRaw.length - 1;
        return {
          ...d,
          sprint,
          ultra,
          sprintPx: Math.round((sprint / max) * this.chartHeight),
          ultraPx: Math.round((ultra / max) * this.chartHeight),
          label: dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          tickLabel: showTick ? dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '',
        };
      });
    },
  },
  mounted() {
    this.loadUsage();
    this.loadDaily();
    this.loadTransactions();
    this.loadWallet('sprint');
    this.loadWallet('ultra');
    this.handleTopupReturn();
  },
  methods: {
    async loadUsage() {
      this.loading = true;
      this.loadError = false;
      try {
        this.items = await fetchTraceyUsageHistory(200);
      } catch (e) {
        console.error('Failed to load Tracey usage history:', e);
        this.loadError = true;
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    async loadTransactions() {
      this.transactionsLoading = true;
      try {
        this.transactions = await fetchTraceyTransactions(200);
      } catch (e) {
        console.error('Failed to load Tracey transactions:', e);
        this.transactions = [];
      } finally {
        this.transactionsLoading = false;
      }
    },
    async loadDaily() {
      this.dailyLoading = true;
      try {
        this.dailyRaw = await fetchTraceyUsageDaily(30);
      } catch (e) {
        console.error('Failed to load Tracey daily usage:', e);
        this.dailyRaw = [];
      } finally {
        this.dailyLoading = false;
      }
    },
    async loadWallet(agent) {
      this.walletLoading = { ...this.walletLoading, [agent]: true };
      try {
        const snap = await fetchTraceyUsage(agent);
        this.walletSnapshots = { ...this.walletSnapshots, [agent]: snap };
      } catch (e) {
        console.warn(`Could not load ${agent} wallet:`, e);
        this.walletSnapshots = { ...this.walletSnapshots, [agent]: null };
      } finally {
        this.walletLoading = { ...this.walletLoading, [agent]: false };
      }
    },
    handleTopupReturn() {
      const topup = this.$route.query.topup;
      if (!topup) return;

      if (topup === 'success') {
        this.topupBanner = { kind: 'success', text: 'Payment received — your credits will appear shortly.' };
        // The webhook that actually grants credits runs async, slightly
        // after Stripe redirects the browser back here, so the balance we
        // just fetched on mount may not reflect it yet. One retry a couple
        // seconds later catches the common case without polling forever.
        // Reload both wallets since the top-up could have been for either
        // agent -- the return URL doesn't say which.
        setTimeout(() => {
          this.loadWallet('ultra');
          this.loadWallet('sprint');
          this.loadTransactions();
        }, 2500);
      } else if (topup === 'cancelled') {
        this.topupBanner = { kind: 'cancelled', text: 'Checkout cancelled — no charge was made.' };
      }

      // Drop ?topup=... from the URL so refreshing the page doesn't re-show
      // the banner or re-trigger the retry.
      const query = { ...this.$route.query };
      delete query.topup;
      this.$router.replace({ query });
    },
    barWidth(snapshot) {
      const allow = Number(snapshot.allowance_credits) || 0;
      const bal = Number(snapshot.balance_credits) || 0;
      if (allow <= 0) return 0;
      return Math.max(0, Math.min(100, (bal / allow) * 100));
    },
    formatResetDate(iso) {
      const d = new Date(iso);
      if (isNaN(d)) return '';
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    },
    formatCredits(n) {
      const v = Number(n) || 0;
      return v.toLocaleString(undefined, { maximumFractionDigits: v < 10 ? 1 : 0 });
    },
    formatDate(ts) {
      if (!ts) return '—';
      const normalized = /([Z]|[+-]\d{2}:\d{2})$/.test(ts) ? ts : ts + 'Z';
      const d = new Date(normalized);
      if (isNaN(d)) return '—';
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    reasonLabel(reason) {
      const labels = {
        grant: 'Monthly grant',
        debit: 'Query',
        reset: 'Monthly reset',
        adjustment: 'Adjustment',
        topup: 'Top-up',
      };
      return labels[reason] || reason;
    },
  },
};
</script>

<style scoped>
.usage-page {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: #f7f8fa;
  padding: 24px;
}

.usage-page__inner {
  max-width: 1100px;
  margin: 0 auto;
}

.usage-page__header {
  margin-bottom: 20px;
}

.usage-page__back {
  text-transform: none;
  letter-spacing: 0;
  color: #667085 !important;
  margin: -4px 0 4px -8px;
}

.usage-page__title {
  font-size: 24px;
  font-weight: 700;
  color: #0d0d0d;
  margin: 0;
}

.usage-page__subtitle {
  margin: 4px 0 0;
  color: #667085;
  font-size: 14px;
}

.usage-page__banner {
  font-size: 13.5px;
  font-weight: 500;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
}

.usage-page__banner--success {
  color: #027a48;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}

.usage-page__banner--cancelled {
  color: #667085;
  background: #f9fafb;
  border: 1px solid #e4e7ec;
}

/* Wallet cards */
.usage-page__wallets {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.wallet-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 16px 18px;
}

.wallet-card__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
}

.wallet-card__name {
  font-size: 13px;
  font-weight: 700;
  color: #101828;
}

.wallet-card__reset {
  margin-top: 10px;
  font-size: 11.5px;
  color: #98a2b3;
}

.wallet-card__balance {
  font-size: 22px;
  font-weight: 700;
  color: #101828;
}

.wallet-card__allowance {
  font-size: 13px;
  font-weight: 500;
  color: #98a2b3;
}

.wallet-card__bar {
  margin-top: 10px;
  height: 6px;
  border-radius: 3px;
  background: #f2f4f7;
  overflow: hidden;
}

.wallet-card__bar-fill {
  height: 100%;
  border-radius: 3px;
}

.wallet-card__bar-fill--empty {
  background: #d92d20 !important;
}

.wallet-card__loading {
  font-size: 13px;
  color: #98a2b3;
}

/* Sections */
.usage-page__section {
  margin-bottom: 24px;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-head h2 {
  font-size: 15px;
  font-weight: 700;
  color: #101828;
  margin: 0;
}

.section-note {
  font-size: 12px;
  color: #98a2b3;
}

.chart-legend {
  display: flex;
  gap: 14px;
}

.chart-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #475467;
}

.chart-legend__item i {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

/* Daily chart */
.chart-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 18px 16px 8px;
  overflow-x: auto;
}

.chart-card__loading {
  font-size: 13px;
  color: #98a2b3;
  padding: 40px 0;
  text-align: center;
}

.daily-chart__bars {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  min-width: 560px;
}

.daily-chart__col {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 8px;
}

.daily-chart__stack {
  width: 100%;
  max-width: 14px;
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-start;
  background: #f2f4f7;
  border-radius: 3px;
  overflow: hidden;
}

.daily-chart__seg {
  width: 100%;
}

.daily-chart__seg--top {
  border-radius: 3px 3px 0 0;
}

.daily-chart__label {
  margin-top: 6px;
  font-size: 10px;
  color: #98a2b3;
  height: 14px;
  white-space: nowrap;
}

.daily-chart__tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #101828;
  color: #fff;
  font-size: 11.5px;
  line-height: 1.6;
  padding: 8px 10px;
  border-radius: 8px;
  white-space: nowrap;
  z-index: 5;
  pointer-events: none;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
}

.daily-chart__tooltip-date {
  font-weight: 700;
  margin-bottom: 2px;
}

.daily-chart__tooltip i {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 2px;
  margin-right: 4px;
}

/* Query history table */
.usage-page__query {
  display: inline-block;
  max-width: 380px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.usage-page__error {
  margin-top: 16px;
  color: #b42318;
  font-size: 14px;
}

/* Transactions table */
.usage-page__amount--positive {
  color: #027a48;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.usage-page__amount--negative {
  color: #667085;
  font-variant-numeric: tabular-nums;
}

.usage-page__muted {
  color: #98a2b3;
}

.usage-page__credits-ultra {
  color: #a3134f;
  font-variant-numeric: tabular-nums;
}

.usage-page__credits-sprint {
  color: #075985;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 640px) {
  .usage-page__wallets {
    grid-template-columns: 1fr;
  }
}
</style>
